import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { useMediaQuery } from '@/lib/useMediaQuery';
import { useBoardColumns } from './useBoardColumns';
import { useBoardView } from './useBoardView';
import { DesktopBoard } from './DesktopBoard';
import { MobileBoard } from './MobileBoard';
import { TaskDetailSheet } from '@/features/tasks/TaskDetailSheet';
import { useTaskById } from '@/features/tasks/useTaskById';
import { BoardSkeleton } from './BoardSkeleton';
import { EmptyState } from '@/components/EmptyState';
import { LiveIndicator } from '@/lib/realtime/RealtimeStatus';
import { BoardToolbar } from './BoardToolbar';
import { SearchOverlay } from './SearchOverlay';
import { TASK_STATUSES, type ColumnFilter, type TaskStatus } from '@/types/api';
import type { Task } from '@/types/api';

export function BoardScreen() {
  const isDesktop = useMediaQuery('(min-width: 1024px)');

  /*
    One filter per column, held here rather than in each column.

    The board owns it because the query lives here — a column that owned its own filter
    would have to own its own query too, and then the five of them could disagree about
    which assignee they were scoped to.
  */
  const [searchOpen, setSearchOpen] = useState(false);

  const [filters, setFilters] = useState<Record<TaskStatus, ColumnFilter>>(() =>
    Object.fromEntries(TASK_STATUSES.map((s) => [s, {}])) as Record<TaskStatus, ColumnFilter>,
  );

  /*
    An admin can narrow the whole board to their own cards. Everyone else has one
    view, and `assigneeId` comes back undefined — the server scopes them regardless,
    so this is a convenience, never the boundary.
  */
  const view = useBoardView();
  const board = useBoardColumns(filters, view.assigneeId);
  /*
    The open card lives in the URL, not in state.

    It has to: the notification bell is in the sidebar, a different subtree entirely,
    and a search param is the one thing both already share. It also makes a card
    linkable — /board?task=<id> can be pasted into Telegram and lands on the card.
  */
  const [searchParams, setSearchParams] = useSearchParams();
  const openTaskId = searchParams.get('task');

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (value === null) next.delete(key);
    else next.set(key, value);
    setSearchParams(next, { replace: true });
  };

  const openTask = (task: Task) => setParam('task', task.id);
  // replace: true on both, so the back button leaves the board rather than walking
  // through every card you opened.
  const closeTask = () => setParam('task', null);

  const fromBoard = board.columns.flatMap((c) => c.tasks).find((t) => t.id === openTaskId) ?? null;

  /*
    The board legitimately does not hold every card — page four of Completed, a column
    never scrolled, or one hidden by a filter. A notification for one of those still
    has to open.
  */
  const fallback = useTaskById(openTaskId && !fromBoard ? openTaskId : null);

  if (board.isPending) return <BoardSkeleton desktop={isDesktop} />;

  if (board.isError) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
        <p className="text-sm font-medium">Could not load the board.</p>
        <p className="max-w-sm text-xs" style={{ color: 'var(--text-muted)' }}>
          {board.error?.message}
        </p>
        <button
          type="button"
          onClick={board.refetch}
          className="rounded-lg border px-4 py-2 text-sm"
          style={{ borderColor: 'var(--border)' }}
        >
          Try again
        </button>
      </div>
    );
  }

  const { total, unread } = board;
  const open = fromBoard ?? fallback.data ?? null;
  // Whose name to put on a card. In "my tasks" every card is yours, so the chip would
  // repeat the same name on every one of them.
  const showAssignee = view.canSwitch && view.fullView;

  return (
    <div className="flex h-full flex-col">
      {/*
        Desktop only. On a phone this was a third stacked bar — app header, this, then
        the chip rail — before a single card. The count it carried now sits beside the
        bell in the app header, where it is visible from every screen rather than only
        this one.
      */}
      <header
        className="hidden shrink-0 items-center gap-x-3 gap-y-1 border-b px-4 py-3 lg:flex"
        style={{ borderColor: 'var(--border)', background: 'var(--surface)' }}
      >
        <div className="min-w-0 flex-1">
          <h1 className="text-base font-semibold tracking-tight">Board</h1>
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
            {showAssignee ? 'All tasks in the company' : 'Tasks assigned to you'}
            {total > 0 && ` · ${total}`}
          </p>
        </div>

        {/*
          Unread gets its own chip rather than another clause in a dot-separated run.
          "12 with new activity" was doing two jobs at once — saying what the number
          meant and hiding it in a sentence — so it read as prose and got skipped.
        */}
        {unread > 0 && (
          <span
            className="shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium tabular-nums"
            style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}
            title="Tasks assigned to you with something you have not read"
          >
            {unread} unread
          </span>
        )}

        {/* Mobile shows this in the app header instead, next to the logo. */}
        <span className="hidden lg:flex">
          <LiveIndicator />
        </span>
      </header>

      {/*
        The board stays mounted while search is open — it dims behind the panel rather
        than being replaced, so looking something up does not cost your scroll position
        or your filters.
      */}
      <BoardToolbar onOpen={() => setSearchOpen(true)} />

      {/* Search answers the same question the board is showing. Narrowed to your own
          cards and then searching the whole company would return results that are
          nowhere on the board behind the panel. */}
      <SearchOverlay
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        onOpenTask={openTask}
        assigneeId={view.assigneeId}
        showAssignee={showAssignee}
      />

      {total === 0 ? (
        <EmptyState
          title="Nothing here yet."
          description={
            view.canSwitch && !view.fullView
              ? 'Nothing is assigned to you. Switch to All tasks to see the whole company.'
              : 'Cards appear as the desks assign them — from Telegram, or anywhere else. They arrive without a refresh.'
          }
        />
      ) : isDesktop ? (
        <DesktopBoard
          board={board}
          onFilter={(status: TaskStatus, filter: ColumnFilter) =>
            setFilters((f) => ({ ...f, [status]: filter }))
          }
          onOpen={openTask}
          showAssignee={showAssignee}
        />
      ) : (
        <MobileBoard
          board={board}
          onFilter={(status: TaskStatus, filter: ColumnFilter) =>
            setFilters((f) => ({ ...f, [status]: filter }))
          }
          onOpen={openTask}
          showAssignee={showAssignee}
        />
      )}

      {/*
        Resolved from the live board rather than held in state, so an update arriving
        while the sheet is open is reflected in it instead of showing a stale copy.
      */}
      <TaskDetailSheet task={open} onClose={closeTask} />
    </div>
  );
}
